// client/pages/TokenDetail.tsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, BarChart3, Activity } from 'lucide-react';
import FixoriumChart from '@/components/FixoriumChart';

// Token symbol and address mapping
const TOKEN_MAP: Record<string, { symbol: string; name: string; address: string }> = {
  SOL: { symbol: "SOL", name: "Solana", address: "So11111111111111111111111111111111111111112" },
  USDT: { symbol: "USDT", name: "Tether", address: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB" },
  USDC: { symbol: "USDC", name: "USD Coin", address: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v" },
  FIXERCOIN: { symbol: "FIXERCOIN", name: "Fixercoin", address: "H4qKn8FMFha8jJuj8xMryMqRhH3h7GjLuxw7TVixpump" },
  PINGX: { symbol: "PINGX", name: "Pingx", address: "7KS4DgKHmgSWYC4uGnSozLUon2bDEj6WKhRNSosmpump" },
  LOCKER: { symbol: "LOCKER", name: "Locker", address: "EN1nYrW6375zMPUkpkGyGSEXW8WmAqYu4yhf6xnGpump" },
  FXM: { symbol: "FXM", name: "FXM", address: "7Fnx57ztmhdpL1uAGmUY1ziwPG2UDKmG6poB4ibjpump" },
  JUP: { symbol: "JUP", name: "Jupiter", address: "JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN" },
  BONK: { symbol: "BONK", name: "Bonk", address: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263" },
};

export default function TokenDetail() {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();
  const [tokenData, setTokenData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const token = symbol ? TOKEN_MAP[symbol.toUpperCase()] : null;
  const tokenSymbol = token?.symbol || symbol?.toUpperCase() || "Token";
  const tokenName = token?.name || "Token";
  const tokenAddress = token?.address || "";

  useEffect(() => {
    // Mock price data for now
    setTokenData({
      price: 91.13,
      priceChange24h: 2.64,
      volume24h: 110751117,
      high24h: 93.05,
      low24h: 89.21,
    });
    setLoading(false);
  }, [tokenAddress]);

  const priceChangeColor = tokenData?.priceChange24h >= 0 ? 'text-green-500' : 'text-red-500';
  const priceChangeSymbol = tokenData?.priceChange24h >= 0 ? '+' : '';

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">{tokenSymbol}</h1>
              <p className="text-sm text-gray-400">{tokenName}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-800">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
              <DollarSign className="w-4 h-4" />
              <span>Price</span>
            </div>
            <div className="text-2xl font-bold text-white">
              ${tokenData?.price?.toFixed(2) || '---'}
            </div>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 border border-slate-800">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
              <TrendingUp className="w-4 h-4" />
              <span>24h Change</span>
            </div>
            <div className={`text-xl font-bold ${priceChangeColor}`}>
              {priceChangeSymbol}{tokenData?.priceChange24h?.toFixed(2) || '0'}%
            </div>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 border border-slate-800">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
              <Activity className="w-4 h-4" />
              <span>24h Volume</span>
            </div>
            <div className="text-lg font-bold text-white">
              ${(tokenData?.volume24h / 1e6).toFixed(2)}M
            </div>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 border border-slate-800">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
              <BarChart3 className="w-4 h-4" />
              <span>24h High/Low</span>
            </div>
            <div className="text-sm font-medium">
              <span className="text-green-500">${tokenData?.high24h?.toFixed(2)}</span>
              <span className="mx-2 text-gray-600">/</span>
              <span className="text-red-500">${tokenData?.low24h?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Chart with increased height on mobile, auto on desktop */}
        <div className="h-80 md:h-auto">
          {tokenAddress ? (
            <FixoriumChart
              tokenAddress={tokenAddress}
              tokenSymbol={tokenSymbol}
              tokenName={tokenName}
            />
          ) : (
            <div className="bg-slate-900 rounded-lg p-8 text-center text-gray-400">
              Chart data not available for {tokenSymbol}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <Button
            onClick={() => navigate(`/swap?token=${tokenSymbol}`)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Trade {tokenSymbol}
          </Button>
          <Button
            onClick={() => navigate(`/withdraw?token=${tokenSymbol}`)}
            variant="outline"
            className="border-slate-700 text-gray-300 hover:text-white hover:border-slate-600"
          >
            Withdraw
          </Button>
        </div>
      </div>
    </div>
  );
}
